import cx from 'classnames';
import {useCallback, useState} from 'react';
import {createPortal} from 'react-dom';
import {Popover} from 'react-tiny-popover';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useTracking} from '~library/context/tracking/tracking-context';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import * as styles from './AccountPreview.module.scss';
import {AccountDetailsDrawer} from './components/AccountDetailsDrawer/AccountDetailsDrawer';
import {UserAvatar} from './UserAvatar';

export const AccountPreview = () => {
  const [isAccountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [mobileAccountSettingsOpen, setMobileAccountSettingsOpen] = useState(false);
  const isLargishAndUp = useIsLargishAndUp();
  const {track} = useTracking();

  const toggleAccountDrawer = useCallback(() => {
    track({
      dataQaId: 'openAccountDetailsButton',
      action: isAccountDrawerOpen ? 'close' : 'open',
    });
    setAccountDrawerOpen((prevState) => !prevState);
  }, [isAccountDrawerOpen, track]);

  const closeAccountDrawer = useCallback(() => {
    setAccountDrawerOpen(false);
    setMobileAccountSettingsOpen(false);
  }, []);

  const openMobileAccountSettings = useCallback(() => {
    track({
      dataQaId: 'account-drawer-mobile-settings',
      action: 'open',
    });
    setMobileAccountSettingsOpen(true);
  }, [track]);

  const closeMobileAccountSettings = useCallback(() => {
    track({
      dataQaId: 'account-drawer-mobile-settings',
      action: 'close',
    });
    setMobileAccountSettingsOpen(false);
  }, [track]);

  return (
    <div className={styles.container}>
      {isLargishAndUp ? (
        <Popover
          isOpen={isAccountDrawerOpen}
          positions={['bottom', 'top', 'right', 'left']}
          padding={10}
          onClickOutside={toggleAccountDrawer}
          content={
            <AccountDetailsDrawer open={isAccountDrawerOpen} onClose={toggleAccountDrawer} />
          }
          containerStyle={{zIndex: '99999'}}
          boundaryElement={document.body}
          align="end"
        >
          <Button
            className={styles.accountDetailsContainer}
            onClick={toggleAccountDrawer}
            data-qa-id="openAccountDetailsButton"
            variant="transparent"
          >
            <UserAvatar />
          </Button>
        </Popover>
      ) : (
        <>
          <Button
            className={styles.accountDetailsContainer}
            onClick={toggleAccountDrawer}
            data-qa-id="openAccountDetailsButton"
            variant="transparent"
          >
            <UserAvatar />
          </Button>

          {createPortal(
            <div
              className={cx(styles.drawer, {
                [styles.open]: isAccountDrawerOpen,
              })}
            >
              <div className={styles.drawerHeader}>
                <Button
                  onClick={
                    mobileAccountSettingsOpen ? closeMobileAccountSettings : closeAccountDrawer
                  }
                  data-qa-id="account-drawer"
                  variant="unstyled-button"
                >
                  <ArrowIcon direction="left" ratio={0.75} />
                </Button>
                {mobileAccountSettingsOpen && (
                  <>
                    <Typography variant="paragraph2" semibold>
                      Account settings
                    </Typography>
                    <span />
                  </>
                )}
              </div>
              <AccountDetailsDrawer
                open={isAccountDrawerOpen}
                onClose={closeAccountDrawer}
                mobileAccountSettingsOpen={mobileAccountSettingsOpen}
                onOpenMobileAccountSettings={openMobileAccountSettings}
                onCloseMobileAccountSettings={closeMobileAccountSettings}
              />
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
};
